import dayjs from 'dayjs';
import { useEffect } from 'react';
import type { DateRange } from 'react-day-picker';

import { DateRangePicker } from '@/components/Form';
import { DateISOFormat } from '@/constants/formats';
import { getDefaultDateRange, setDefaultDateRange } from '@/helper/user-preferences';

import { usePageFilterContext } from './PageFilterProvider';
import { type PageFiltersType } from './types';

const getLast90Days = () => {
  return {
    startDate: dayjs().subtract(90, 'd').startOf('d').format(DateISOFormat),
    endDate: dayjs().endOf('d').format(DateISOFormat),
  };
};

const addDefaultDatesIntoFiltersIfNecessary = (pageFilters: PageFiltersType) => {
  // We may not have a `startDate` and `endDate` in the pageFilters until the filters get pushed
  // down into the component. This is a workaround to ensure that the date range is set to what the
  // filters will eventually contain.
  if (!pageFilters.startDate && !pageFilters.endDate) {
    const defaultRange = getDefaultDateRange() || getLast90Days();
    return {
      ...pageFilters,
      ...defaultRange,
    };
  } else {
    return pageFilters;
  }
};

const DashboardDateRangeFilter = () => {
  const { pageFilters, setPageFilters } = usePageFilterContext();

  // Push the default start/end dates into the filters context so other components can use them.
  useEffect(() => {
    const defaultRange = getDefaultDateRange();

    if (defaultRange) {
      setPageFilters(defaultRange);
    } else {
      const newDefaultRange = getLast90Days();
      setDefaultDateRange(newDefaultRange);
      setPageFilters(newDefaultRange);
    }
  }, [setPageFilters]);

  const handleDateRangeChange = (value: DateRange | undefined) => {
    if (!value) {
      return;
    }

    const { from, to } = value;

    const newDateRange = {
      startDate: dayjs(from).format(DateISOFormat),
      endDate: dayjs(to).format(DateISOFormat),
    };
    setDefaultDateRange(newDateRange);
    setPageFilters(newDateRange);
  };

  const filtersWithDateRange = addDefaultDatesIntoFiltersIfNecessary(pageFilters);

  return (
    <DateRangePicker
      value={{
        from: dayjs(filtersWithDateRange.startDate).toDate(),
        to: dayjs(filtersWithDateRange.endDate).toDate(),
      }}
      onValueChange={handleDateRangeChange}
    />
  );
};

export default DashboardDateRangeFilter;
